import { Text } from '@eo-locale/react';
import download from 'downloadjs';
import { FC, useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import { CardApi, UserApi } from '../api';
import { CreditCard } from 'lucide-react';

import { ReactComponent as UploadIcon } from '../assets/icons/upload.svg';
import {
  Card,
  InnerPage,
  InnerPageContent,
  InnerPageSection,
  Portal,
} from '../components';
import styles from './CardPage.module.css';

import { useCardTypes } from '../hooks/useCardTypes';

export const CardPage: FC = () => {
  const { data: user } = useQuery('user', UserApi.fetch);
  const { data: cardTypeOptions = [] } = useCardTypes();

  const currentCardTypeLabel = useMemo(() => {
    const ct = String(user?.card_type ?? '0');
    const arr = Array.isArray(cardTypeOptions) ? cardTypeOptions : [];
    return arr.find(o => String(o.value) === ct)?.label ?? 'Nie wybrano';
  }, [cardTypeOptions, user?.card_type]);

  const requiredAccepted = useMemo(() => {
    if (!user?.agreements) return false;
    const required = user.agreements.filter(a => Number(a.required) === 1);
    if (required.length === 0) return true;
    return required.every(a => Number(a.has_agree) === 1);
  }, [user?.agreements]);

  const canChangeType = useMemo(() => {
    const ct = Number(user?.card_type ?? 0);
    return requiredAccepted && (ct === 0 || ct === 1);
  }, [requiredAccepted, user?.card_type]);

  const downloadCard = useCallback(async () => {
    try {
      const data = await CardApi.pdf();
      download(data, 'card.pdf', 'application/pdf');
    } catch (error) {
      console.error(error);
    }
  }, []);

  const openChangeType = useCallback(() => {
    const ev = new CustomEvent('open-card-type-modal');
    window.dispatchEvent(ev);
  }, []);

  return (
    <InnerPage>
      <InnerPageContent className="relative">
        <InnerPageSection>
          <Portal containerId="mobile-header">
            <UploadIcon onClick={downloadCard} />
          </Portal>

          <div className={styles.actions_row}>
            <button
              className={styles.download}
              onClick={downloadCard}
              type="button"
            >
              <UploadIcon />
              <Text id="download" />
            </button>

            {canChangeType && (
              <button
                className={styles.change_type}
                type="button"
                onClick={openChangeType}
              >
                <CreditCard size={16} style={{ marginRight: 8 }} />
                <Text id="change_card_type" defaultMessage="Zmień typ legitymacji" />
              </button>
            )}
          </div>

          <div className={styles.currentTypeNote}>
            <small>
              <Text id="current_card_type" defaultMessage="Aktualny typ legitymacji" />:{' '}
              <strong>{currentCardTypeLabel}</strong>
            </small>
          </div>

          <div className={styles.card_wrapper}>
            <Card />
          </div>
        </InnerPageSection>
      </InnerPageContent>
    </InnerPage>
  );
};